/*!
 * liveScenePlan.js — what a change of scene does: which sounds come in and go,
 * what leaves the stage, which lines are taken back
 * © 2026 Rob Canning — GPLv3
 *
 * A scene is a #$ block with a name (#$ drop). Its lines make its sounds and
 * what it draws (liveEval.js namesOf). Going from one scene to the next: what
 * only the old one made goes -- its sounds faded out and stopped, its shapes,
 * texts, pictures, film and camera faded and taken away (liveEval.js undoFor,
 * as commenting the lines out and running them does) -- and what only the new
 * one makes comes in. A name both make carries across: the new line changes it
 * in place. Pure, for liveScenes.js.
 */

import { namesOf, undoFor } from "./liveEval.js";
import { isFoldHead, foldSpan } from "./liveFold.js";

/** "#$ drop" -> "drop"; a head that is not a name ("#$ -- drums --") -> null. */
export const sceneNameOf = line => /^\s*#\$+\s*([A-Za-z_][\w-]*)\s*$/.exec(String(line ?? ""))?.[1]
  ?? /^\s*#\$+\s*([A-Za-z_][\w-]*)\b/.exec(String(line ?? ""))?.[1] ?? null;

const uniq = list => [...new Set(list)];

/**
 * From the old scene's lines to the new one's: { soundsIn, soundsOut,
 * drawnIn, drawnOut, undo } -- undo the lines that take back what only the
 * old scene made.
 */
export function scenePlan(oldExprs = [], newExprs = []) {
  const read = list => list.map(expr => ({ expr, ...namesOf(expr) }));
  const olds = read(oldExprs), news = read(newExprs);
  const names = list => new Set(list.flatMap(n => [...n.sounds, ...n.drawn]));
  const oldNames = names(olds), newNames = names(news);
  const shared = n => [...n.sounds, ...n.drawn, ...n.targets].some(x => newNames.has(x));
  return {
    soundsIn: uniq(news.flatMap(n => n.sounds)).filter(s => !oldNames.has(s)),
    soundsOut: uniq(olds.flatMap(n => n.sounds)).filter(s => !newNames.has(s)),
    drawnIn: uniq(news.flatMap(n => n.drawn)).filter(s => !oldNames.has(s)),
    drawnOut: uniq(olds.flatMap(n => n.drawn)).filter(s => !newNames.has(s)),
    undo: olds.filter(n => !shared(n)).flatMap(n => undoFor(n.expr)),
  };
}

/** The scenes in a buffer's lines: [{ name, head, from, to }] -- the head's line and its block's. */
export function sceneSpans(lines) {
  const out = [];
  lines.forEach((line, head) => {
    const name = isFoldHead(line) ? sceneNameOf(line) : null;
    const span = name ? foldSpan(lines, head) : null;
    if (span) out.push({ name, head, from: span[0], to: span[1] });
  });
  return out;
}

/**
 * Running the whole buffer (Ctrl+Shift+Enter, an example opening): in a
 * buffer that calls scene() outside its scenes, the scenes' lines are left
 * for scene() to start -- else every scene would start at once. Which lines
 * (from 0) to leave; none in a buffer without scene().
 */
export function linesLeftToScenes(lines, startLines) {
  const spans = sceneSpans(lines);
  if (!spans.length) return new Set();
  const inScene = n => spans.some(s => n >= s.from && n <= s.to);
  const callsScene = lines.some((l, n) => !inScene(n) && /^\s*scene\s*\(/.test(l));
  return new Set(callsScene ? startLines.filter(inScene) : []);
}

// ---- wait(): a block's lines in turn (liveScenes.js runInTurn) ----------------------

const waitSteps = new Map();    // wait(Pseq(...)) -> how far along it is, run after run

/**
 * The beats a wait() line asks for, or null when the line is no wait():
 * wait(16); wait(rand(8, 24)); wait(choose(8, 16, 32)) or wait(Prand(8, 16, 32, inf)),
 * one of them each run; wait(Pseq(8, 16, 32, inf)) the next one each run.
 */
export function waitBeats(expr) {
  const m = /^\s*wait\s*\(\s*([\s\S]*?)\s*\)\s*;?\s*$/i.exec(String(expr ?? ""));
  if (!m) return null;
  const v = m[1];
  const nums = s => s.split(",").map(x => x.trim()).filter(x => !/^inf$/i.test(x)).map(Number).filter(Number.isFinite);
  let r;
  if ((r = /^rand\s*\(([^)]*)\)$/i.exec(v))) {
    const [a, b = a] = nums(r[1]);
    return a + Math.random() * (b - a);
  }
  if ((r = /^(choose|Prand|Pxrand|Pshuf|Pwhite)\s*\(([^)]*)\)$/i.exec(v))) {
    const xs = nums(r[2]);
    if (/^Pwhite$/i.test(r[1])) { const [a, b = a] = xs; return a + Math.random() * (b - a); }
    return xs.length ? xs[Math.floor(Math.random() * xs.length)] : 0;
  }
  if ((r = /^Pseq\s*\(([^)]*)\)$/i.exec(v))) {
    const xs = nums(r[1]);
    if (!xs.length) return 0;
    const i = waitSteps.get(expr) ?? 0;
    waitSteps.set(expr, i + 1);
    return xs[i % xs.length];
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

